class PriceRangeSliderComponent extends HTMLElement {
  constructor() {
    super();
    this.minValue = 0;
    this.maxValue = 1000;
    this.currentMin = 0;
    this.currentMax = 1000;
    this.isDragging = false;
    this.activeHandle = null;
    this.sliderWidth = 0;
    this.step = 1;
    this.debounceTimeout = null;
    this.hasDragged = false;
  }

  connectedCallback(silent = false) {
    if (this.dataset.filterStyle !== 'vertical') {
      return;
    }
    
    this.initializeSlider();
    this.setupEventListeners();
    this.updateSlider(false, false);
    setTimeout(() => {
      const minPercentage = ((this.currentMin - this.minValue) / (this.maxValue - this.minValue)) * 100;
      const maxPercentage = ((this.currentMax - this.minValue) / (this.maxValue - this.minValue)) * 100;
      this.updateTooltipPositions(minPercentage, maxPercentage);
    }, 100);
    
    this.watchForResize();
  }

  watchForResize() {
    const resizeObserver = new ResizeObserver(() => {
      const minPercentage = ((this.currentMin - this.minValue) / (this.maxValue - this.minValue)) * 100;
      const maxPercentage = ((this.currentMax - this.minValue) / (this.maxValue - this.minValue)) * 100;
      this.updateTooltipPositions(minPercentage, maxPercentage);
    });
    
    const trackElement = this.refs.range?.parentElement;
    if (trackElement) {
      resizeObserver.observe(trackElement);
    }
    
    this._resizeObserver = resizeObserver;
  }

  initializeSlider() {
    try {
      const priceFacetComponent = this.closest('accordion-custom')?.querySelector('price-facet-component');
      if (!priceFacetComponent) {
        console.warn('Price-facet-component not found for slider initialization');
        return;
      }
      
      const minInput = priceFacetComponent.querySelector('input[ref="minInput"]');
      const maxInput = priceFacetComponent.querySelector('input[ref="maxInput"]');
      
      if (!minInput || !maxInput) {
        console.warn('Price facet inputs not found:', { minInput: !!minInput, maxInput: !!maxInput });
        return;
      }
      
      if (minInput) {
        this.minValue = Math.round(parseFloat(minInput.getAttribute('data-min')) || 0);
        this.maxValue = Math.round(parseFloat(minInput.getAttribute('data-max')) || 1000);
        this.currentMin = Math.round(parseFloat(minInput.value) || this.minValue);
      }
      
      if (maxInput) {
        this.maxValue = Math.round(parseFloat(maxInput.getAttribute('data-max')) || 1000);
        this.currentMax = Math.round(parseFloat(maxInput.value) || this.maxValue);
      }

      this.originalMinInput = minInput;
      this.originalMaxInput = maxInput;

      if (minInput) minInput.style.display = 'none';
      if (maxInput) maxInput.style.display = 'none';
      this.innerHTML = `
        <div class="price-range-slider">
          <div class="price-range-slider__track">
            <div class="price-range-slider__range" ref="range"></div>
            <div class="price-range-slider__handle price-range-slider__handle--min" ref="minHandle" tabindex="0"></div>
            <div class="price-range-slider__handle price-range-slider__handle--max" ref="maxHandle" tabindex="0"></div>
            <!-- Tooltips positioned independently inside track (hidden initially) -->
            <span class="price-range-slider__tooltip" ref="minTooltip" style="opacity: 0; left: var(--tooltip-min-position, 0%);"></span>
            <span class="price-range-slider__tooltip" ref="maxTooltip" style="opacity: 0; left: var(--tooltip-max-position, 0%);"></span>
          </div>
          <div class="price-range-slider__labels">
            <span class="price-range-slider__label price-range-slider__label--min" ref="minLabel"></span>
            <span class="price-range-slider__label price-range-slider__label--max" ref="maxLabel"></span>
          </div>
        </div>
      `;

      this.refs = {
        range: this.querySelector('[ref="range"]'),
        minHandle: this.querySelector('[ref="minHandle"]'),
        maxHandle: this.querySelector('[ref="maxHandle"]'),
        minTooltip: this.querySelector('[ref="minTooltip"]'),
        maxTooltip: this.querySelector('[ref="maxTooltip"]'),
        minLabel: this.querySelector('[ref="minLabel"]'),
        maxLabel: this.querySelector('[ref="maxLabel"]')
      };
      
      // Restore tooltip positions if they exist
      if (window.priceSliderPositions) {
        const minPosition = window.priceSliderPositions['--tooltip-min-position'];
        const maxPosition = window.priceSliderPositions['--tooltip-max-position'];
        
        if (minPosition && this.refs.minTooltip) {
          this.refs.minTooltip.style.left = minPosition;
          this.refs.minTooltip.style.opacity = '1';
        }
        
        if (maxPosition && this.refs.maxTooltip) {
          this.refs.maxTooltip.style.left = maxPosition;
          this.refs.maxTooltip.style.opacity = '1';
        }
      }
      
      // Ensure handles are enabled and loader is hidden on initialization
      this.enableHandles();
      this.hideLoader();
    } catch (error) {
      console.error('Error initializing slider:', error);
    }
  }

  setupEventListeners() {
    try {
      if (!this.refs || !this.refs.minHandle || !this.refs.maxHandle || !this.refs.range) {
        console.warn('Slider refs not available for event listeners');
        return;
      }
      
      // Mouse events
      this.refs.minHandle.addEventListener('mousedown', (e) => this.startDrag(e, 'min'));
      this.refs.maxHandle.addEventListener('mousedown', (e) => this.startDrag(e, 'max'));
      
      // Touch events (with passive: false to allow preventDefault)
      this.refs.minHandle.addEventListener('touchstart', (e) => this.startDrag(e, 'min'), { passive: false });
      this.refs.maxHandle.addEventListener('touchstart', (e) => this.startDrag(e, 'max'), { passive: false });
      
      // Keyboard events
      this.refs.minHandle.addEventListener('keydown', (e) => this.handleKeydown(e, 'min'));
      this.refs.maxHandle.addEventListener('keydown', (e) => this.handleKeydown(e, 'max'));
      
      // Global events
      document.addEventListener('mousemove', this.handleDrag.bind(this));
      document.addEventListener('mouseup', this.endDrag.bind(this));
      document.addEventListener('touchmove', this.handleDrag.bind(this), { passive: false });
      document.addEventListener('touchend', this.endDrag.bind(this));
      
      // Track click events
      this.refs.range.parentElement.addEventListener('click', this.handleTrackClick.bind(this));
    } catch (error) {
      console.error('Error setting up event listeners:', error);
    }
  }

  startDrag(event, handle) {
    // Check if handles are disabled
    if (this.refs[handle + 'Handle'].classList.contains('price-range-slider__handle--disabled')) {
      event.preventDefault();
      return;
    }
    
    event.preventDefault();
    this.isDragging = true;
    this.activeHandle = handle;
    this.sliderWidth = this.refs.range.parentElement.offsetWidth;
    
    // Add dragging class for styling
    this.refs[handle + 'Handle'].classList.add('price-range-slider__handle--dragging');
    
    // Focus the handle for accessibility
    this.refs[handle + 'Handle'].focus();
  }

  handleDrag(event) {
    if (!this.isDragging || !this.activeHandle) return;
    
    event.preventDefault();
    
    // Mark that the user has actually dragged
    this.hasDragged = true;
    
    const clientX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX;
    const rect = this.refs.range.parentElement.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / this.sliderWidth));
    
    this.updateValue(this.activeHandle, percentage);
  }

  endDrag() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.refs[this.activeHandle + 'Handle'].classList.remove('price-range-slider__handle--dragging');
    this.activeHandle = null;
    
    // Only start debounce timer if the user has actually dragged
    if (this.hasDragged) {
      this.startDebounceTimer();
      this.hasDragged = false; // Reset for next drag
    }
    
    // Trigger change event
    this.dispatchEvent(new CustomEvent('change', { bubbles: true }));
  }

  handleTrackClick(event) {
    if (this.isDragging) return;
    
    const rect = this.refs.range.parentElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / this.sliderWidth));
    const value = Math.round(this.minValue + (percentage * (this.maxValue - this.minValue)));
    
    // Determine which handle is closer
    const minDistance = Math.abs(value - this.currentMin);
    const maxDistance = Math.abs(value - this.currentMax);
    
    if (minDistance < maxDistance) {
      this.updateValue('min', percentage);
    } else {
      this.updateValue('max', percentage);
    }
    
    // Mark that the user has interacted (clicked track)
    this.hasDragged = true;
    
    // Start debounce timer since track click changes values
    this.startDebounceTimer();
    
    this.dispatchEvent(new CustomEvent('change', { bubbles: true }));
  }

  handleKeydown(event, handle) {
    const step = (this.maxValue - this.minValue) / 100; // 1% steps
    
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.currentMin = Math.max(this.minValue, this.currentMin - step);
        this.updateSlider();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.currentMin = Math.min(this.currentMax - step, this.currentMin + step);
        this.updateSlider();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (handle === 'max') {
          this.currentMax = Math.min(this.maxValue, this.currentMax + step);
        } else {
          this.currentMin = Math.min(this.currentMax - step, this.currentMin + step);
        }
        this.updateSlider();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (handle === 'max') {
          this.currentMax = Math.max(this.currentMin + step, this.currentMax - step);
        } else {
          this.currentMin = Math.max(this.minValue, this.currentMin - step);
        }
        this.updateSlider();
        break;
      case 'Home':
        event.preventDefault();
        if (handle === 'min') {
          this.currentMin = this.minValue;
        } else {
          this.currentMax = this.maxValue;
        }
        this.updateSlider();
        break;
      case 'End':
        event.preventDefault();
        if (handle === 'min') {
          this.currentMin = this.currentMax - step;
        } else {
          this.currentMax = this.minValue + step;
        }
        this.updateSlider();
        break;
    }
    
    // Mark that the user has interacted (keyboard)
    this.hasDragged = true;
    
    // Start debounce timer since keyboard changes values
    this.startDebounceTimer();
    
    this.dispatchEvent(new CustomEvent('change', { bubbles: true }));
  }

  updateValue(handle, percentage) {
    const value = this.minValue + (percentage * (this.maxValue - this.minValue));
    const roundedValue = Math.round(value);
    
    if (handle === 'min') {
      this.currentMin = Math.max(this.minValue, Math.min(this.currentMax - this.step, roundedValue));
    } else {
      this.currentMax = Math.min(this.maxValue, Math.max(this.currentMin + this.step, roundedValue));
    }
    
    this.updateSlider();
  }

  updateSlider(updateInputs = false, showTooltips = true) {
    const minPercentage = ((this.currentMin - this.minValue) / (this.maxValue - this.minValue)) * 100;
    const maxPercentage = ((this.currentMax - this.minValue) / (this.maxValue - this.minValue)) * 100;
    
    // Update range bar
    this.refs.range.style.left = minPercentage + '%';
    this.refs.range.style.width = (maxPercentage - minPercentage) + '%';
    
    // Update handles
    this.refs.minHandle.style.left = minPercentage + '%';
    this.refs.maxHandle.style.left = maxPercentage + '%';
    
    // Calculate tooltip positions to keep them within container bounds
    this.updateTooltipPositions(minPercentage, maxPercentage, showTooltips);
    
    // Update tooltips with current values
    this.refs.minTooltip.textContent = this.formatMoney(this.currentMin);
    this.refs.maxTooltip.textContent = this.formatMoney(this.currentMax);
    
    // Update labels with static min/max values
    this.refs.minLabel.textContent = this.formatMoney(this.minValue);
    this.refs.maxLabel.textContent = this.formatMoney(this.maxValue);
    
    // Only update original inputs and trigger filter changes when explicitly requested
    if (updateInputs) {
      if (this.originalMinInput) {
        this.originalMinInput.value = Math.round(this.currentMin);
      }
      if (this.originalMaxInput) {
        this.originalMaxInput.value = Math.round(this.currentMax);
      }
      
      // Trigger change event on the price-facet-component to use existing functionality
      const priceFacetComponent = this.closest('accordion-custom')?.querySelector('price-facet-component');
      if (priceFacetComponent && typeof priceFacetComponent.updatePriceFilterAndResults === 'function') {
        priceFacetComponent.updatePriceFilterAndResults();
      }
    }
  }

  updateTooltipPositions(minPercentage, maxPercentage, showTooltips = true) {
    const trackElement = this.refs.range.parentElement; // .price-range-slider__track
    if (!trackElement) return;

    const trackWidth = trackElement.offsetWidth;
    
    // Calculate each tooltip position independently relative to track
    this.updateSingleTooltipPosition(this.refs.minTooltip, minPercentage, trackWidth, showTooltips);
    this.updateSingleTooltipPosition(this.refs.maxTooltip, maxPercentage, trackWidth, showTooltips);
  }

  updateSingleTooltipPosition(tooltip, handlePercentage, containerWidth, showTooltips = true) {
    // Get tooltip width
    const tooltipWidth = tooltip.offsetWidth || 40;
    
    // Calculate tooltip boundaries as percentages
    const tooltipHalfWidthPercentage = (tooltipWidth / 2) / containerWidth * 100;
    const minAllowedPercentage = tooltipHalfWidthPercentage;
    const maxAllowedPercentage = 100 - tooltipHalfWidthPercentage;
    
    // Calculate desired tooltip position (centered with handle)
    let tooltipPercentage = handlePercentage;
    
    // Clamp tooltip position to stay within container bounds
    tooltipPercentage = Math.max(minAllowedPercentage, Math.min(tooltipPercentage, maxAllowedPercentage));
    
    // Always update positioning (regardless of showTooltips)
    const variableName = tooltip === this.refs.minTooltip ? '--tooltip-min-position' : '--tooltip-max-position';
    document.body.style.setProperty(variableName, tooltipPercentage + '%');
    
    // Also store in a global object for debugging
    if (!window.priceSliderPositions) {
      window.priceSliderPositions = {};
    }
    window.priceSliderPositions[variableName] = tooltipPercentage + '%';
    
    // Always update the tooltip position directly
    tooltip.style.left = tooltipPercentage + '%';
    
    // Only control visibility with showTooltips
    if (showTooltips) {
      setTimeout(() => {
        tooltip.style.opacity = '1';
      }, 50); // Small delay to ensure positioning is complete
    }
  }

  startDebounceTimer() {
    // Clear any existing debounce timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    
    // Set a new debounce timeout to wait for user to stop interacting
    this.debounceTimeout = setTimeout(() => {
      // Show loader AFTER 300ms of no interaction
      this.showLoader();
      
      // Disable handle dragging
      this.disableHandles();
      
      // Update inputs and trigger filter changes
      if (this.originalMinInput) {
        this.originalMinInput.value = Math.round(this.currentMin);
      }
      if (this.originalMaxInput) {
        this.originalMaxInput.value = Math.round(this.currentMax);
      }
      
      // Trigger change event on the price-facet-component to use existing functionality
      const priceFacetComponent = this.closest('accordion-custom')?.querySelector('price-facet-component');
      if (priceFacetComponent && typeof priceFacetComponent.updatePriceFilterAndResults === 'function') {
        priceFacetComponent.updatePriceFilterAndResults();
      }
      
      this.debounceTimeout = null;
    }, 300); // Wait 300ms after user stops interacting
  }

  showLoader() {
    // Add loader to tooltips
    if (this.refs.minTooltip) {
      this.refs.minTooltip.innerHTML = this.formatMoney(this.currentMin) + '<span class="price-range-slider__loader"></span>';
    }
    if (this.refs.maxTooltip) {
      this.refs.maxTooltip.innerHTML = this.formatMoney(this.currentMax) + '<span class="price-range-slider__loader"></span>';
    }
  }

  hideLoader() {
    // Remove loader from tooltips
    if (this.refs.minTooltip) {
      this.refs.minTooltip.textContent = this.formatMoney(this.currentMin);
    }
    if (this.refs.maxTooltip) {
      this.refs.maxTooltip.textContent = this.formatMoney(this.currentMax);
    }
    
    // Re-enable handle dragging
    this.enableHandles();
  }

  disableHandles() {
    // Disable handle dragging by adding a disabled class
    if (this.refs.minHandle) {
      this.refs.minHandle.classList.add('price-range-slider__handle--disabled');
      this.refs.minHandle.style.pointerEvents = 'none';
    }
    if (this.refs.maxHandle) {
      this.refs.maxHandle.classList.add('price-range-slider__handle--disabled');
      this.refs.maxHandle.style.pointerEvents = 'none';
    }
  }

  enableHandles() {
    // Re-enable handle dragging by removing disabled class
    if (this.refs.minHandle) {
      this.refs.minHandle.classList.remove('price-range-slider__handle--disabled');
      this.refs.minHandle.style.pointerEvents = 'auto';
    }
    if (this.refs.maxHandle) {
      this.refs.maxHandle.classList.remove('price-range-slider__handle--disabled');
      this.refs.maxHandle.style.pointerEvents = 'auto';
    }
  }

  formatMoney(value) {
    // Get currency symbol from the page
    const currencySymbol = document.querySelector('.price-facet__label')?.textContent || '$';
    return currencySymbol + value.toFixed(0);
  }

  disconnectedCallback() {
    // Clean up observers
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  }

  // Public methods
  setRange(min, max) {
    this.minValue = min;
    this.maxValue = max;
    this.updateSlider();
  }

  setValues(min, max) {
    this.currentMin = Math.max(this.minValue, min);
    this.currentMax = Math.min(this.maxValue, max);
    this.updateSlider();
  }

  getValues() {
    return {
      min: this.currentMin,
      max: this.currentMax
    };
  }
}

// Register the component
if (!customElements.get('price-range-slider-component')) {
  customElements.define('price-range-slider-component', PriceRangeSliderComponent);
}